import { Router, RouterModule, Routes } from '@angular/router';
import { inject, NgModule } from '@angular/core';
import { isSuperAdmin } from './guards/super-admin.guard';
import { AuthService } from './services/auth.service';
import { GuestLayoutComponent } from './components/layouts/guest-layout.component';
import { authGuard } from './guards/auth.guard';
import { UserLayoutComponent } from './components/layouts/user-layout.component';
import { telefoonStartGuard } from './guards/telefoon-start.guard';
import { geenSalesGuard, isSalesViewer } from './guards/sales-viewer.guard';


const routes: Routes = [
  {
    // Sales (sales_viewer, 15-10-2026): alleen Numbers, en dan zonder de
    // schil eromheen. Zijbalk, statusregel en onderbalk vragen bij het
    // openen van alles op (clubs, aanvragen, vloot) waar deze rol niet bij
    // mag. Voor de andere rollen valt deze route weg en staat Numbers
    // gewoon in de schil, hieronder.
    path: 'numbers',
    canMatch: [() => isSalesViewer(inject(AuthService).user)],
    canActivate: [authGuard],
    loadComponent: () => import('./pages/numbers/numbers.component').then(c => c.NumbersComponent),
  },
  {
    path: '', component: UserLayoutComponent, canActivate: [authGuard, geenSalesGuard],
    children: [
      {
        path: '',
        canActivate: [telefoonStartGuard],
        loadChildren: () => import('./pages/dashboard/dashboard.module').then(m => m.DashboardModule)
      },
      {
        path: 'users',
        loadChildren: () => import('./pages/users/users.module').then(m => m.UsersModule)
      },
      {
        // Instellen hoort bij de club, kijken bij Monitoring. Zelfde scherm,
        // andere stand: hier de modules per pc en wie de studio mag bedienen;
        // op /studio alleen wat de pc's melden (besluit 02-09-2026).
        // Vóór 'clubs', anders vangt die route het pad af.
        path: 'clubs/:clubId/devices',
        loadChildren: () => import('./pages/club-studio/club-studio.module').then(m => m.ClubStudioModule),
        data: { stand: 'devices' }
      },
      {
        path: 'clubs',
        loadChildren: () => import('./pages/clubs/clubs.module').then(m => m.ClubsModule)
      },
      {
        // Overzicht van één league: speelschema per ronde, wat er aan elke
        // wedstrijd hangt, clubs, en wat ontbreekt (28-09-2026). Leest met
        // het "Open als club"-token van de league, dus alleen super-admins.
        // Vóór 'leagues', anders vangt die route het pad af.
        path: 'leagues/:league/overview',
        canActivate: [() => isSuperAdmin(inject(AuthService).user) || inject(Router).createUrlTree(['/'])],
        loadComponent: () => import('./pages/league-overview/league-overview.component').then(c => c.LeagueOverviewComponent),
      },
      {
        // Wedstrijden van alle clubs en opdrachten voor taggingteams
        // (docs/TAGGER-OPDRACHTEN.md, 30-09-2026). Alleen super-admins.
        path: 'matches',
        canActivate: [() => isSuperAdmin(inject(AuthService).user) || inject(Router).createUrlTree(['/'])],
        loadComponent: () => import('./pages/tagging/tagging-matches.component').then(c => c.TaggingMatchesComponent),
      },
      {
        path: 'tagging-teams',
        canActivate: [() => isSuperAdmin(inject(AuthService).user) || inject(Router).createUrlTree(['/'])],
        loadComponent: () => import('./pages/tagging/tagging-teams.component').then(c => c.TaggingTeamsComponent),
      },
      {
        // Aanvragen van clubs voor een tegenstander die nergens staat
        // (01-10-2026). Goedkeuren schrijft met ?club= in arch, dus alleen
        // super-admins.
        path: 'club-requests',
        canActivate: [() => isSuperAdmin(inject(AuthService).user) || inject(Router).createUrlTree(['/'])],
        loadComponent: () => import('./pages/club-requests/club-requests.component').then(c => c.ClubRequestsComponent),
      },
      {
        // Aanvragen vanaf het inlogscherm (22-10-2026): goedkeuren maakt een
        // account in elke club, dus alleen super-admins. De mail van elke
        // aanvraag linkt hierheen.
        path: 'account-requests',
        canActivate: [() => isSuperAdmin(inject(AuthService).user) || inject(Router).createUrlTree(['/'])],
        loadComponent: () => import('./pages/account-requests/account-requests.component').then(c => c.AccountRequestsComponent),
      },
      {
        // League-omgevingen: zelfde ClubsModule, andere URL → listKind=league.
        path: 'leagues',
        loadChildren: () => import('./pages/clubs/clubs.module').then(m => m.ClubsModule)
      },
      {
        path: 'federations',
        loadChildren: () => import('./pages/federations/federations.module').then(m => m.FederationsModule)
      },
      {
        // Alle spelers van alle clubs (catalogue), naast de global players.
        path: 'players',
        loadChildren: () => import('./pages/players/players.module').then(m => m.PlayersModule)
      },
      {
        path: 'global-players',
        loadChildren: () => import('./pages/global-players/global-players.module').then(m => m.GlobalPlayersModule)
      },
      {
        // MySQL ClubCatalogEntry (CSV + API-Football), incl. logo-URL. Super_admin.
        path: 'club-catalog',
        canActivate: [() => isSuperAdmin(inject(AuthService).user) || inject(Router).createUrlTree(['/'])],
        loadChildren: () => import('./pages/club-catalog/club-catalog.module').then(m => m.ClubCatalogModule)
      },
      {
        path: 'teams',
        loadChildren: () => import('./pages/teams/teams.module').then(m => m.TeamsModule)
      },
      {
        path: 'integrations',
        loadChildren: () => import('./pages/api/api.module').then(m => m.ApiModule)
      },
      { path: 'api', redirectTo: 'integrations', pathMatch: 'full' },
      {
        path: 'videos',
        loadChildren: () => import('./pages/videos/videos.module').then(m => m.VideosModule)
      },
      {
        path: 'licences',
        loadChildren: () => import('./pages/licences/licences.module').then(m => m.LicencesModule)
      },
      {
        path: 'producers',
        loadChildren: () => import('./pages/producers/producers.module').then(m => m.ProducersModule)
      },
      {
        path: 'recorders',
        loadChildren: () => import('./pages/recorders/recorders.module').then(m => m.RecordersModule)
      },
      {
        path: 'studio',
        loadChildren: () => import('./pages/club-studio/club-studio.module').then(m => m.ClubStudioModule)
      },
      {
        path: 'roles',
        loadChildren: () => import('./pages/roles/roles.module').then(m => m.RolesModule)
      },
      {
        path: 'security',
        loadChildren: () => import('./pages/security/security.module').then(m => m.SecurityModule)
      },
      {
        path: 'fleet',
        loadChildren: () => import('./pages/fleet/fleet.module').then(m => m.FleetModule)
      },
      {
        path: 'sportcam',
        loadChildren: () => import('./pages/sportcam/sportcam.module').then(m => m.SportcamModule)
      },
      {
        // Gebruik van het platform per club en per persoon (15-10-2026).
        path: 'platform-usage',
        loadChildren: () => import('./pages/platform-usage/platform-usage.module').then(m => m.PlatformUsageModule)
      },
      {
        path: 'studio-usage',
        loadChildren: () => import('./pages/studio-usage/studio-usage.module').then(m => m.StudioUsageModule)
      },
      {
        // Cijfers voor een salesgesprek (15-10-2026). Super- en support-admins.
        path: 'numbers',
        loadComponent: () => import('./pages/numbers/numbers.component').then(c => c.NumbersComponent),
      },
      {
        path: 'clubs-stats',
        loadChildren: () => import('./pages/clubs-stats/clubs-stats.module').then(m => m.ClubsStatsModule)
      },
      {
        path: 'schedules',
        loadChildren: () => import('./pages/schedules/schedules.module').then(m => m.SchedulesModule)
      },
      {
        path: 'panels',
        loadChildren: () => import('./pages/panels/panels.module').then(m => m.PanelsModule),
      },
      {
        path: 'monitoring',
        loadChildren: () => import('./pages/monitoring/monitoring.module').then(m => m.MonitoringModule)
      },
      {
        path: 'tracking',
        loadChildren: () => import('./pages/tracking/tracking.module').then(m => m.TrackingModule)
      },
      {
        // COMET-matchsheets in bulk (28-09-2026). Alleen super-admins: de
        // import schrijft via "Open als club" in de league-omgevingen.
        path: 'comet-import',
        canActivate: [() => isSuperAdmin(inject(AuthService).user) || inject(Router).createUrlTree(['/'])],
        loadComponent: () => import('./pages/comet-import/comet-import.component').then(c => c.CometImportComponent),
      },
      {
        // Proefimport per bron: niets opslaan, alleen laten zien wat een
        // import zou maken (01-10-2026).
        path: 'data-imports',
        canActivate: [() => isSuperAdmin(inject(AuthService).user) || inject(Router).createUrlTree(['/'])],
        loadComponent: () => import('./pages/data-imports/data-imports.component').then(c => c.DataImportsComponent),
      },
      {
        // Onboarding: taken per profiel, tutorialvideo's en de antwoorden
        // van nieuwe gebruikers (docs/ONBOARDING.md, 07-10-2026). Alleen
        // super-admins; de auth-dienst weigert anderen ook.
        path: 'onboarding',
        canActivate: [() => isSuperAdmin(inject(AuthService).user) || inject(Router).createUrlTree(['/'])],
        loadComponent: () => import('./pages/onboarding/onboarding.component').then(c => c.OnboardingComponent),
      },
    ]
  },
  {
    // Mobile claim page — the target of the QR a recorder shows when
    // unactivated. Login-gated (super_admin claims), returnUrl-aware.
    // Deliberately OUTSIDE the admin shell (UserLayoutComponent): it is
    // opened on a phone from the QR scan, so it renders as a standalone
    // full-screen mobile page without the desktop sidebar/topbar.
    path: 'claim',
    canActivate: [authGuard, geenSalesGuard],
    loadChildren: () => import('./pages/claim/claim.module').then(m => m.ClaimModule)
  },
  {
    path: '', component: GuestLayoutComponent,
    children: [
      {
        path: 'login',
        loadChildren: () => import('./pages/login/login.module').then(m => m.LoginModule)
      }
    ]
  },
  {
    path: 'public-schedules',
    loadChildren: () => import('./pages/schedules-public/schedules-public.module').then(m => m.SchedulesPublicModule)
  },
  {path: '**', redirectTo: ''}
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule]
})
export class AppRoutingModule {
}
